import { useState, type ReactNode } from 'react';
import { CaretIcon } from '@rsos/assets/icons';
import styles from './TowProFormSection.module.css';

interface TowProFormSectionProps {
  title: string;
  children: ReactNode;
  defaultExpanded?: boolean;
  /** Render the section as a rounded, outlined card. */
  bordered?: boolean;
  /** Prefix for the section's data-name hooks, for forms other than TowPro's. */
  dataNamePrefix?: string;
}

/**
 * Collapsible request form section with an uppercase header and a caret
 * that toggles the body. Expansion is local UI state.
 */
function TowProFormSection({
  title,
  children,
  defaultExpanded = true,
  bordered = false,
  dataNamePrefix = 'towpro',
}: TowProFormSectionProps): JSX.Element {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  return (
    <section
      className={`${styles.section}${bordered ? ` ${styles.bordered}` : ''}`}
      data-name={`${dataNamePrefix}-form-section-${title.toLowerCase().replace(/\W+/g, '-')}`}
    >
      <button
        type="button"
        className={styles.header}
        aria-expanded={isExpanded}
        onClick={() => setIsExpanded(prev => !prev)}
        data-name={`${dataNamePrefix}-form-section-toggle`}
      >
        {title}
        <span
          className={`${styles.caret}${isExpanded ? '' : ` ${styles.caretCollapsed}`}`}
        >
          <CaretIcon color="primaryText" width={11} height={8} />
        </span>
      </button>
      {isExpanded && <div className={styles.content}>{children}</div>}
    </section>
  );
}

export default TowProFormSection;
